import React from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { SoftwareEnumSchema } from "@domain/schemas/BI";
import type { SupportedSoftware } from "@domain/repositories/ISoftwareVersionsRepository";
import { useLicensedSoftware } from "./useLicensedSoftware";

type SoftwareOption = {
  label: string;
  value: SoftwareEnumSchema;
};

const softwareOptions: SoftwareOption[] = [
  { label: "Spectronaut", value: SoftwareEnumSchema.Spectronaut },
  { label: "SpectroMine", value: SoftwareEnumSchema.SpectroMine },
];

/**
 * Search engine selection for a new job.
 *
 * Only engines with a license present in the cluster are offered. Changing the
 * engine clears the selected version, because versions are image tags of
 * engine-specific repositories and are therefore not interchangeable.
 */
export const SoftwareSelectionFormBlock: React.FC = () => {
  const software = useJobSetupStore((state) => state.CreateJobState.software ?? SoftwareEnumSchema.Spectronaut);
  const setSoftware = useJobSetupStore((state) => state.setSoftware);
  const setSoftwareVersion = useJobSetupStore((state) => state.setSoftwareVersion);

  const licenseState = useLicensedSoftware();

  const availableOptions = React.useMemo(
    () => (licenseState.status === "ready" ? softwareOptions.filter((option) => licenseState.licensed.includes(option.label as SupportedSoftware)) : []),
    [licenseState],
  );

  // Keep the store in step with what is actually selectable.
  React.useEffect(() => {
    if (availableOptions.length === 0 || availableOptions.some((option) => option.value === software)) return;

    setSoftware(availableOptions[0].value);
    setSoftwareVersion("");
  }, [availableOptions, setSoftware, setSoftwareVersion, software]);

  const selectedSoftware = availableOptions.find((option) => option.value === software) ?? null;

  return (
    <fieldset className="k-form-fieldset">
      <legend className="k-form-legend">
        <strong>Software</strong>
      </legend>
      {licenseState.status === "loading" && <div style={{ color: "#64748b" }}>Checking licenses...</div>}
      {licenseState.status === "unknown" && <div style={{ color: "#64748b" }}>Waiting for the cluster to report license status...</div>}
      {licenseState.status === "error" && (
        <div role="alert" style={{ color: "#b00020" }}>
          Could not read the license status. Retrying...
        </div>
      )}
      {licenseState.status === "ready" && availableOptions.length === 0 && <div style={{ color: "#b45309" }}>No licensed search engine. Add a license key on the About page.</div>}
      {availableOptions.length > 0 && (
        <DropDownList
          style={{ width: "100%" }}
          data={availableOptions}
          textField="label"
          dataItemKey="value"
          value={selectedSoftware}
          onChange={(event) => {
            const nextSoftware = event.value?.value as SoftwareEnumSchema | undefined;
            if (!nextSoftware || nextSoftware === software) {
              return;
            }

            setSoftware(nextSoftware);
            setSoftwareVersion("");
          }}
        />
      )}
    </fieldset>
  );
};
